/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { alias, setDiff } from '@ember/object/computed';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import {
  filterBy,
  findBy,
  mapBy,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { getDynamicStyles } from '@linc-technologies/linced-common/utils/table';
import moment from 'moment';

export default class ReportPrinter extends Component {
  @action
  setFilterFrom(value) {
    this.filterFrom = value;
  }

  @action
  setFilterTo(value) {
    this.filterTo = value;
  }

  // Services
  @service
  options;

  @service
  store;

  @service
  toaster;

  PersonType = PersonType;
  Scopes = Scopes;
  Jurisdiction = Jurisdiction;

  @computed('school')
  get reportPageName() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_PAGE_NAME,
    );
  }

  // mode sets the print mode.  Should be either `report`, `page` or `post`.
  @tracked mode = 'report';

  // currentYear is the year for which a report is being generated.
  @tracked currentYear = moment().year();

  // endTime is a timestamp, to be fed into any components which fetch/filter
  // their own data.
  endTime = null;

  // landscape determines whether to print in landscape orientation.
  landscape = false;

  // isParentView specifies whether to render the report in parent view.
  @tracked isParentView = false;

  // school specifies the School for which the report/content is being printed.
  @tracked school = null;

  // person specifies the Person whose report/content is being printed.
  person = null;

  // page specifies the Page to be printed in `page` mode.
  @tracked page = null;

  // post specifies the Post to be printed in `post` mode.
  post = null;

  // pages specifies the Pages to be printed in `report` mode.
  pages = null;

  // posts specifies the Posts to be printed in modes other than `post`.
  posts = null;

  // allPosts is used internally to stage posts to be printed in "page" mode.
  allPosts = null;

  // includeDraftPosts determines whether to include draft/unpublished posts.
  @tracked includeDraftPosts = false;

  // includeLearnerPosts determines whether to include posts by the learner.
  @tracked includeLearnerPosts = true;

  // includeCaregiverPosts determines whether to include posts by caregivers.
  @tracked includeCaregiverPosts = true;

  // filterPostsByDate determines whether to filter posts by date.
  @tracked filterPostsByDate = false;

  // filterFrom enables filtering posts by date.
  @tracked filterFrom = '';

  // filterTo enables filtering posts by date.
  @tracked filterTo = '';

  // filterPostsByTag determines whether to filter posts by tag.
  @tracked filterPostsByTag = false;

  // filterTags enables filtering posts by tag.
  @tracked
  filterTags = [];

  // levels specifies the Progress Levels to use to generate the report.
  levels = null;

  // expectations is a map of subject ID to array of Expectations.
  expectations = null;

  // progressions is a map of subject ID to array of Progressions.
  progressions = null;

  // learnerSummaries is a map of subject ID to learner subject summary.
  learnerSummaries = null;

  // grades specifies the grades to be included in the report.
  grades = null;

  // numberOfAssessmentTerms specifies how many terms to show grades for.
  numberOfAssessmentTerms = 4;

  // unassignedSubjects specifies any unassigned Pages to be printed in
  // `report` mode.
  unassignedSubjects = null;

  // goalSubjects is a list of goal subjects which are accessible to the user,
  // to avoid issues for users (i.e. caregivers) who don't have access to all.
  goalSubjects = null;

  // developingStates is a list of recent Goal States for in-progress goals.
  developingStates = null;

  // secureStates is a list of Goal States for recently completed goals.
  secureStates = null;

  // plpLabel is the school's label for PLP Goals.
  plpLabel = 'Personal Learning Plan';

  // plpGoalStates is the list of States for PLP goals to show in the report.
  plpGoalStates = null;

  // attendanceTerms is a list of terms to display attendance dials for.
  attendanceTerms = null;

  // includeProfile specifies whether to include the student profile details.
  @tracked
  includeProfile = true;

  // includeProfileImage specifies whether to show the student profile image.
  @tracked
  includeProfileImage = true;

  // includeReportDescription specifies whether the report description should
  // be included at the start of the report.
  @tracked
  includeReportDescription = true;

  // includePageDescription specifies whether the Page description should be
  // included at the start of the Page.
  @tracked
  includePageDescription = true;

  // includeDescriptionImage specifies whether the image associated with the
  // page description should be included
  @tracked
  includeDescriptionImage = true;

  // includeAuthorImages specifies whether to show authors' profile images.
  @tracked
  includeAuthorImages = true;

  // includeImages specifies whether to show images attached to Posts.
  @tracked
  includeImages = true;

  // includeOtherMedia specifies whether to list other media attached to Posts.
  @tracked
  includeOtherMedia = false;

  // includeComments specifies whether to show comments on Posts.
  @tracked
  includeComments = false;

  // includeTagsOverview specifies whether to include a Tags Overview.
  @tracked
  includeTagsOverview = false;

  // includeLegalName specifies whether to include the students legal name.
  @tracked
  includeLegalName = false;

  // includeUnlinkedPosts specifies whether to include the unlinked posts.
  @tracked includeUnlinkedPosts = true;

  @tracked
  includeSchoolLogo = true;
  @tracked
  includeSchoolAddress = false;
  @tracked
  includePostHeader = true;
  @tracked
  includeSignature = false;

  // printing flags whether printing has been initiated.
  @tracked
  printing = false;

  // terms specifies the terms for the reports grade overviews
  terms = null;

  // timeframes specifies the timeframes for the reports grade overviews
  timeframes = null;

  // onClose is the action to perform to close the dialog.
  onClose() {}

  @computed('mode', 'page', 'currentYear', 'reportPageName')
  get title() {
    switch (this.mode) {
      case 'page':
        return this.get('page.title');

      case 'report':
        return this.reportPageName || `${this.currentYear} Report`;

      case 'post':
        return this.get('post.page.title');
    }
    return '';
  }

  @computed('school')
  get principalSignature() {
    return this.options.schoolGet(this.school, 'nswDOE:letters:signature');
  }

  // The {{toggle}} helper sets the 2 overview switches. A set value overrides
  // the school option.
  @tracked _includeAssessmentOverview;
  @tracked _includeAssessmentOverviewOnHomepage;

  get includeAssessmentOverview() {
    return (
      this._includeAssessmentOverview ??
      this.options.schoolGet(this.school, Options.ASSESSMENTS_SHOW_OVERVIEW)
    );
  }

  set includeAssessmentOverview(value) {
    this._includeAssessmentOverview = value;
  }

  get includeAssessmentOverviewOnHomepage() {
    return (
      this._includeAssessmentOverviewOnHomepage ??
      this.options.schoolGet(
        this.school,
        Options.ASSESSMENTS_SHOW_HOMEPAGE_OVERVIEW,
      )
    );
  }

  set includeAssessmentOverviewOnHomepage(value) {
    this._includeAssessmentOverviewOnHomepage = value;
  }

  @alias('school.jurisdiction')
  jurisdiction;

  @computed(
    'principalSignature',
    'jurisdiction',
    'page.isSuspensionLetters',
    'post.page.isSuspensionLetters',
  )
  get signatureAvailable() {
    if (this.jurisdiction === Jurisdiction.AU_NSW) {
      if (
        (this.get('page.isSuspensionLetters') && this.mode === 'post') ||
        this.get('post.page.isSuspensionLetters')
      ) {
        if (!isEmpty(this.principalSignature)) {
          return true;
        }
      }
    }
    return false;
  }

  // preFilteredPosts applies the first level of filters to posts.
  @computed(
    'includeDraftPosts',
    'includeLearnerPosts',
    'includeCaregiverPosts',
    'includeUnlinkedPosts',
    'posts.@each.{status,author,assessment_id}',
  )
  get preFilteredPosts() {
    if (
      this.includeDraftPosts &&
      this.includeLearnerPosts &&
      this.includeCaregiverPosts &&
      this.includeUnlinkedPosts
    ) {
      // No need to filter if we're including everything.
      return this.posts;
    }

    return this.posts.filter((post) => {
      if (!this.includeDraftPosts && post.status !== PostStatus.PUBLISHED) {
        // Post isn't published, and we only want published posts.
        return false;
      }

      if (!this.includeUnlinkedPosts && isEmpty(post.get('assessment_id'))) {
        // Post in unlinked, and we only want linked posts
        return false;
      }

      let authorType = post.get('author.type');
      if (authorType === PersonType.LEARNER && !this.includeLearnerPosts) {
        // Post is by the learner, and we don't want learner posts.
        return false;
      }
      if (authorType === PersonType.CAREGIVER && !this.includeCaregiverPosts) {
        // Post is by a caregiver, and we don't want caregiver posts.
        return false;
      }

      // All other posts should be included.
      return true;
    });
  }

  // dateFilteredPosts filters the list of posts by date (if enabled).
  @computed(
    'filterPostsByDate',
    'preFilteredPosts.@each.date',
    'filterFrom',
    'filterTo',
  )
  get dateFilteredPosts() {
    if (!this.filterPostsByDate) {
      // Date filtering not enabled.
      return this.preFilteredPosts;
    }
    if (isEmpty(this.filterFrom) && isEmpty(this.filterTo)) {
      // No date filters.  Don't bother filtering.
      return this.preFilteredPosts;
    }

    let filterFrom, filterTo;
    if (!isEmpty(this.filterFrom)) {
      filterFrom = moment(this.filterFrom).unix();
    }
    if (!isEmpty(this.filterTo)) {
      // Use 11:59:59pm to include posts on the selected date.
      filterTo = moment(this.filterTo)
        .set({ hour: 23, minute: 59, second: 59 })
        .unix();
    }
    return this.preFilteredPosts.filter((post) => {
      if (!isEmpty(filterFrom) && post.date < filterFrom) {
        // Post is dated before the selected "from" date.
        return false;
      }
      if (!isEmpty(filterTo) && post.date > filterTo) {
        // Post is dated after the selected "to" date.
        return false;
      }
      // Post is dated within the selected range.
      return true;
    });
  }

  // filteredPosts applies any tag filters (if enabled) to the date-filtered
  // posts provided by `dateFilteredPosts`.
  @computed(
    'mode',
    'filterPostsByTag',
    'filterTags.[]',
    'preFilteredPosts.[]',
    'dateFilteredPosts.[]',
  )
  get filteredPosts() {
    if (this.mode !== 'page') {
      // Only "page" mode allows filtering posts by tag/date.
      return this.preFilteredPosts;
    }
    if (!this.filterPostsByTag || isEmpty(this.filterTags)) {
      // Not filtering by tag (or none chosen); return all posts within date
      // range (or all visible posts, if no date filter is applied).
      return this.dateFilteredPosts;
    }

    let tagIds = mapBy(filterBy(this.filterTags, 'type', 'tag'), 'entity.id');
    let goalIds = mapBy(filterBy(this.filterTags, 'type', 'goal'), 'entity.id');
    return this.dateFilteredPosts.filter((post) => {
      // tags and goals are async hasManys, whose PromiseManyArrays have no
      // some(); the ids are all that's needed.
      if (
        post
          .hasMany('tagIds')
          .ids()
          .some((id) => tagIds.includes(id))
      ) {
        // Post has a tag we're filtering for.
        return true;
      }
      if (
        post
          .hasMany('goalIds')
          .ids()
          .some((id) => goalIds.includes(id))
      ) {
        // Post has a goal we're filtering for.
        return true;
      }

      // No matching tag or goal.
      return false;
    });
  }

  // allTags provides a list of tags and goals which are used on posts.
  @computed('dateFilteredPosts.@each.{tags,goals}')
  get allTags() {
    if (isEmpty(this.dateFilteredPosts)) {
      return [];
    }
    return uniqBy(
      this.dateFilteredPosts.reduce((tags, post) => {
        if (!isEmpty(post.tags)) {
          post.tags.forEach((tag) => {
            tags.push({
              type: 'tag',
              entity: tag,
              label: tag.label,
            });
          });
        }
        if (!isEmpty(post.goals)) {
          post.goals.forEach((goal) => {
            tags.push({
              type: 'goal',
              entity: goal,
              label: goal.peopleDescription,
            });
          });
        }
        return tags;
      }, []),
      'entity',
    );
  }

  // availableTags lists tags from the page which haven't been selected.
  @setDiff('allTags', 'filterTags')
  availableTags;

  // reportDescription returns the school set report page description.
  get reportDescription() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_DESCRIPTION,
    );
  }

  get reportMedia() {
    return this.options.schoolGet(this.school, Options.REPORTING_REPORT_MEDIA);
  }

  @tracked
  reportImage = null;

  @computed('isParentView', 'page.{media,caregiverMedia}')
  get pageMedia() {
    if (this.page) {
      if (this.isParentView) {
        return this.page.caregiverMedia;
      }
      return this.page.media;
    }
  }

  @computed('isParentView', 'page.{description,caregiverDescription}')
  get pageDescription() {
    if (this.page) {
      if (this.isParentView) {
        return this.page.caregiverDescription;
      }
      return this.page.description;
    }
  }

  @computed('developingStates.@each.goal_subject_id')
  get developingSubjectIds() {
    if (!isArray(this.developingStates)) {
      return [];
    }
    let subjectIds = this.developingStates.map(
      (state) => state.goal_subject_id,
    );
    return [...new Set(subjectIds)];
  }

  @computed('secureStates.@each.goal_subject_id')
  get secureSubjectIds() {
    if (!isArray(this.secureStates)) {
      return [];
    }
    return [
      ...new Set(this.secureStates.map((state) => state.goal_subject_id)),
    ];
  }

  @computed('grades.@each.assessment_id')
  get gradedAssessmentIds() {
    if (!isArray(this.grades)) {
      return [];
    }
    return [...new Set(this.grades.map((grade) => grade.assessment_id))];
  }

  // breakBeforePages determines whether we'll add a page break before starting
  // on Pages, based on how much other content precedes Pages.
  @computed('mainBlocks.[]', 'unassignedSubjects.[]', 'plpGoalStates.[]')
  get breakBeforePages() {
    let pagesIndex = this.mainBlocks.indexOf('pages');
    let plpIndex = this.mainBlocks.indexOf('plpGoals');
    let goalsIndex = this.mainBlocks.indexOf('unassignedSubjects');
    let hasManyPlpGoals =
      isArray(this.plpGoalStates) && this.plpGoalStates.length > 10;
    if (plpIndex < pagesIndex && hasManyPlpGoals) {
      // We've got a decent number of PLP goals, and they're shown before
      // Pages.  Add a page break to avoid an orphaned Page title.
      return true;
    }
    if (
      goalsIndex < pagesIndex &&
      this.unassignedSubjects.some((subject) => {
        if (subject.trackable) {
          // Progressions are enabled.
          return true;
        }
        if (
          this.developingSubjectIds.includes(subject.id) ||
          this.secureSubjectIds.includes(subject.id)
        ) {
          // We have at least one developing or secure goal to show.
          return true;
        }
        return false;
      })
    ) {
      // We have at least one unassigned subject which we expect to show.
      // Add a page break to avoid an orphaned Page title.
      return true;
    }

    // We probably don't need to add a page break before Pages.
    return false;
  }

  // reportPages filters the provided pages to remove any with no content.
  @computed(
    'mode',
    'showSubjectsOnReport',
    'pages.[]',
    'filteredPosts.[]',
    'goalSubjects.[]',
    'developingSubjectIds.[]',
    'secureSubjectIds.[]',
    'gradedAssessmentIds.[]',
  )
  get reportPages() {
    if (isArray(this.pages)) {
      return this.pages.filter((page) => {
        if (!isEmpty(findBy(this.filteredPosts, 'page_id', page.id))) {
          // We have at least one post to show.
          return true;
        }
        if (
          this.showSubjectsOnReport &&
          isArray(page.goalSubject_ids) &&
          isArray(this.goalSubjects) &&
          this.goalSubjects.some((subject) => {
            if (!page.goalSubject_ids.includes(subject.id)) {
              // Subject not to be shown on this Page.
              return false;
            }
            if (subject.trackable) {
              // Progressions are enabled.
              return true;
            }
            if (
              this.developingSubjectIds.includes(subject.id) ||
              this.secureSubjectIds.includes(subject.id)
            ) {
              // We have at least one developing or secure goal to show.
              return true;
            }
          })
        ) {
          // We have at least one subject to show.
          return true;
        }
        if (page.hasAssessments && isArray(page.assessment_ids)) {
          // assessment_ids can be a fragment array, which has no some().
          for (const assessmentId of page.assessment_ids) {
            if (this.gradedAssessmentIds.includes(assessmentId)) {
              // There's at least one Assessment on this Page.
              return true;
            }
          }
        }
      });
    }
    return [];
  }

  get mainBlocks() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_MAIN_BLOCKS,
    );
  }

  get pageBlocks() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_PAGE_BLOCKS,
    );
  }

  get numPostsPerPage() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_POSTS_PER_PAGE,
    );
  }

  @computed('school')
  get showSubjectsOn() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_SHOW_SUBJECTS_ON,
    );
  }

  @computed('showSubjectsOn')
  get showSubjectsOnReport() {
    if (isEmpty(this.showSubjectsOn)) {
      // Show by default if the option isn't set yet.
      return true;
    }
    return ['report', 'both'].includes(this.showSubjectsOn);
  }

  @computed('school', 'jurisdiction')
  get periods() {
    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      return 'Days';
    }
    return this.options.schoolGet(
      this.school,
      Options.ATTENDANCE_PERIODS_LABEL,
    );
  }

  @computed('school')
  get _showAttendance() {
    return this.options.schoolGet(
      this.school,
      Options.ATTENDANCE_SHOW_TO_CAREGIVERS,
    );
  }

  get showAttendance() {
    return this.isParentView ? this._showAttendance : true;
  }

  // printStyles generates the base (static) styles for report printing.
  printStyles(landscape) {
    return `
      <link rel="stylesheet" href="${
        window.location.origin
      }/assets/icons/linced-material.css">
      <style type="text/css">
        body {
          font-family: -apple-system, BlinkMacSystemFont, Roboto, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
        }

        h1, h2, h3, h4 {
          page-break-after: avoid;
        }
        h3 {
          margin-bottom: 0;
        }

        .page-break {
          page-break-before: always;
        }

        .layout-row {
          display: flex;
          align-items: flex-start;
          flex-wrap: wrap;
        }
        .layout-column {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .layout-column > div{
          width: 100%;
        }
        .layout-align-start-center {
          align-items: center;
        }
        .flex {
          flex: 1;
        }

        .school-banner {
          display: flex;
          align-items: center;
          padding-bottom: 8px;
          border-bottom: 1px solid grey;
        }
        .school-banner .logo {
          height: 60px;
          margin-right: 10px;
        }
        .school-banner h1 {
          font-weight: normal;
          margin: 0;
        }
        .school-address-banner {
          width: 100%;
          padding-bottom: 8px;
          border-bottom: 1px solid grey;
        }
        .school-address-banner h4 {
          font-weight: normal;
          margin: 0;
          width: 100%;
          display: block;
        }

        .profile-banner-card {
          display: flex;
          align-items: flex-start;
          padding: 10px 0 12px 2px;
        }
        .profile-banner__image {
          margin-right: 12px;
        }
        .profile-banner-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .profile-banner-card h2,
        .profile-banner-card h3,
        .profile-banner-card h4 {
          margin: 0;
        }
        .profile-banner-card h3 {
          font-size: 125%;
        }
        .profile-banner-card h4 {
          padding-left: 2px;
          font-weight: normal;
        }
        .profile-banner-card h2 .jurisdictional-id {
          font-weight: normal;
          font-size: 75%;
        }
        .report-intro {
          page-break-inside: avoid;
        }
        .report-intro:after {
          content: '';
          display: block;
          clear: both;
        }
        .report-intro img {
          max-width: 33%;
          max-height: 150px;
          margin-right: 12px;
          float: left;
        }
        .report-intro-image img {
          max-height: 150px;
        }

        .report-intro ul,
        .report-intro ol {
          overflow: hidden;
        }

        img.round {
          border-radius: 50%;
          object-fit: cover;
        }

        .report-intro,
        .report-intro-image,
        .attendance,
        .posts,
        .goals,
        .progress,
        .assessment {
          margin-bottom: 36px;
        }

        .page {
          page-break-after: always;
        }

        h2.page-title {
          font-size: 1.35em;
        }

        .post {
          margin-bottom: 18px;
        }
        .post-header {
          display: flex;
          align-items: flex-start;
          page-break-inside: avoid;
          margin-top: 12px;
        }
        .post-header img {
          margin: 4px 8px 0 0;
        }
        .post-header-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .post-header h3 {
          margin: 0;
        }
        .post-header h4 {
          margin: 0 0 2px;
          font-weight: 500;
        }
        .post .gallery {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
        }
        .post .gallery div {
          page-break-inside: avoid;
        }
        .post .gallery img {
          max-height: 150px;
          margin: 8px 16px 8px 0;
        }
        .post p {
          margin: 8px 0;
        }
        .post > h4 {
          margin: 8px 0 4px;
        }
        .post .attachments {
          margin-top: 4px;
          padding-left: 28px;
        }
        h4.post-comments-count {
          margin: 2px 0;
        }
        .post-comment {
          font-size: 80%;
          page-break-inside: avoid;
          margin-bottom: 6px;
        }
        .post-comment .post-comment {
          margin-top: 3px;
          font-size: 100%;
        }
        .post-comment .layout-row {
          flex-wrap: nowrap;
        }
        img.comment-author {
          margin-right: 6px;
        }
        span.comment-author {
          font-weight: bold;
        }
        .post-comment-replies {
          padding-left: 14px;
        }

        .goals .goals-dials {
          page-break-inside: avoid;
        }
        .goals h3 {
          margin-bottom: 0;
        }
        .goals h4 {
          break-after: avoid-page;
          margin: 4px 0;
        }
        .goals ul {
          break-before: avoid-page;
          margin: 4px 0 8px;
          padding-left: 28px;
        }
        .goals ul > li:first-child {
          break-before: avoid-page;
        }

        .md-chips md-chip {
          border-radius: 16px;
          display: block;
          min-height: 32px;
          line-height: 32px;
          margin: 0 4px;
          padding-right: 8px;
          border: 1px solid #aeaeae;
        }
        .md-chips md-chip .md-chip-content {
          display: flex;
          align-items: flex-start;
        }
        .md-chips md-chip .md-chip-content img {
          height: 32px;
          border-radius: 16px;
        }
        .md-chips md-chip .md-chip-content .goal-details-label {
          padding-left: 8px;
        }
        .md-chips.post-tags md-chip {
          display: inline-block;
          padding-left: 8px;
          margin: 4px;
          min-height: 22px;
          line-height: 22px;
          font-size: 0.8em;
        }

        .attendance .chart-pie {
          zoom: 0.75;
        }

        .chart-pie .arc text {
          font: 10px sans-serif;
          text-anchor: middle;
        }
        .chart-pie .arc path {
          stroke: #fff;
        }
        .chart-pie .hole-label {
          text-anchor: middle;
          font-weight: bold;
        }
        .chart-pie .secure-percentage,
        .chart-pie .developing-percentage {
          text-anchor: middle;
          font: 10px sans-serif;
        }
        .chart-pie .secure-percentage {
          fill: #fff;
        }

        .chart svg {
          flex: none;
        }
        .chart .legend {
          padding-left: 12px;
          page-break-inside: avoid;
          max-height: none !important;
        }
        .chart .legend li {
          list-style-type: none;
        }
        .chart .legend .legend-chip {
          display: inline-block;
          border: 1px solid #000;
          width: 12px;
          height: 12px;
          margin-right: 3px;
        }

        .progress {
          page-break-inside: avoid;
        }
        .progress p {
          margin: 0.5em 0;
        }
        /* Achievement statements use parent-hide if in caregiver view. */
        .progress .parent-hide {
          display: none;
        }

        .progress-chart-legend {
          font-size: 0.75em;
        }
        .progress-chart-legend > div {
          padding: 2px 8px;
        }
        .progress-chart-legend > div > span {
          margin-left: 3px;
        }

        .progress-legend {
          display: inline-block;
          width: 24px;
          height: 0;
          border-top: 2px solid #000;
          border-bottom: 6px solid rgba(0, 0, 0, 0.3);
        }

        .progress-line-legend {
          display: inline-block;
          width: 24px;
          height: 2px;
          border-bottom: 2px solid #000;
        }
        .progress-line-legend:after {
          content: '';
          display: block;
          height: 0;
          width: 0;
          margin-left: 9px;
          border: 3px solid #000;
          border-radius: 50%;
        }

        .assessment {
          page-break-inside: avoid;
        }
        .tags-overview {
          page-break-before: always;
        }
        .assessment table,
        .tags-overview-category table,
        .attendance-individual-table table{
          width: 100%;
          border-collapse: collapse;
        }
        .assessment th.md-column,
        .tags-overview-category th.md-column,
        .attendance th.md-column {
          text-align: left;
        }
        .tags-overview-category th.md-column br {
          display: none;
        }
        .assessment th,
        .assessment td,
        .tags-overview-category th,
        .tags-overview-category td {
          padding: 3px;
        }
        .assessment tr th:not(:last-child),
        .assessment tr td:not(:last-child),
        .tags-overview-category tr th:not(:last-child),
        .tags-overview-category tr th:not(:last-child) {
          padding-right: 12px;
        }
        .assessment table tbody tr,
        .tags-overview-category table tbody tr,
        .attendance-individual-table tbody tr,
        .attendance-individual-table tfoot tr {
          border-top: 1px solid #aeaeae;
        }

        .tags-overview-category h3,
        .tags-overview-category h4 {
          margin: 8px 0 4px;
        }

        .tags-overview-charts {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .tags-overview-chart {
          width: 48%;
          page-break-inside: avoid;
        }
        .tags-overview-chart.wide {
          width: auto;
        }
        .tags-overview-chart .layout-row {
          flex-wrap: nowrap;
        }
        .goals-progress__container {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
          grid-column-gap: 20px;

          .md-chips md-chip {
            min-width: 90px;
            text-align: center;
            .md-chip-content {
              float: none;
            }
          }
          md-chips span.goal-details-label,
          .md-contact-chips .md-chips md-chip .md-contact-name {
            display: inline;
            text-align: center;
          }
        }
        #principal_signature__image {
          max-height: 60px;
          margin: 16px 0;
        }
        ${landscape ? `@media print{@page {size: landscape}}` : ``}
        #post_content_spacer {
          height: 24px;
        }
        #return_slip {
          page-break-before: always;
        }

        .goals .goals-dials .progress-dot,
        .goals .grade-chips md-chip.md-chip {
          display: inline-block;
          width: 18px;
          height: 18px;
          min-height: 18px;
          box-sizing: border-box;
          overflow: hidden;
          padding: 2px 4px;
          border-radius: 50%;
          color: #fff;
        }
        .goals .goals-dials .progress-dot {
          margin: 22px 0 0 8px;
        }

        .progress md-chips.achievement-chip md-chip {
          display: inline-block;
          padding-left: 8px;
          margin-top: 4px;
        }

        .goals .goal-grader__circle {
          display: inline-block;
          box-sizing: border-box;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          border-width: 9px;
          border-style: solid;
        }
      </style>
    `;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    if (
      (this.get('page.isSuspensionLetters') && this.mode === 'post') ||
      this.get('post.page.isSuspensionLetters')
    ) {
      this.includeProfile = false;
      this.includeLegalName = false;
      this.includeProfileImage = false;
      this.includePostHeader = false;
      this.includeAuthorImages = false;
      this.includeImages = false;
      this.includeOtherMedia = false;
      this.includeComments = false;
      this.includeSchoolLogo = true;
      this.includeSchoolAddress = true;
      this.includeSignature = true;
    }

    if (this.mode === 'report' && !isEmpty(this.reportMedia)) {
      this.store.findRecord('media', this.reportMedia).then((media) => {
        if (media.mediaType === MediaType.IMAGE) {
          this.reportImage = media;
        }
      });
    }
    if (this.mode === 'page' && !isEmpty(this.posts)) {
      let sortedPosts = this.posts
        .slice()
        .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));

      // Set the default filter dates to encompass all provided posts.
      this.filterFrom = moment.unix(sortedPosts[0].date).format('YYYY-MM-DD');

      this.filterTo = moment.unix(sortedPosts.at(-1).date).format('YYYY-MM-DD');

      // Include draft posts by default on "closed" pages.
      this.includeDraftPosts = !(
        this.get('page.visibleToParents') || this.get('page.visibleToLearners')
      );
    }
  }

  // resetOptions resets the print options in order to provide a consistent
  // starting point.
  resetOptions() {
    this.filterPostsByDate = false;
    this.filterFrom = '';
    this.filterTo = '';
    this.filterPostsByTag = false;
    this.filterTags = [];
    this.includeProfile = true;
    this.includeProfileImage = true;
    this.includeReportDescription = true;
    this.includePageDescription = true;
    this.includeDescriptionImage = true;
    this.includeAuthorImages = true;
    this.includeImages = true;
    this.includeOtherMedia = false;
    this.includeComments = false;
    this.includeTagsOverview = false;
    this.includeSchoolLogo = false;
    this.includeSchoolAddress = false;
    this.includePostHeader = true;
    this.printing = false;
  }

  @action
  closeDialog() {
    this.resetOptions();
    this.onClose();
  }

  async insertSignature(printHTML) {
    if (!isEmpty(this.principalSignature)) {
      let signature = await this.store.findRecord(
        'media',
        this.principalSignature,
      );

      const placeholderRegex = /<div id="principal_signature">[^]*?<\/div>/;
      // Make the image with the DOM. Thus the attribute values get quotes and
      // escapes.
      const img = document.createElement('img');
      img.id = 'principal_signature__image';
      img.src = signature.url;
      img.alt = signature.altText;
      img.title = signature.altText;

      return printHTML.replace(placeholderRegex, () => img.outerHTML);
    }
  }

  @action
  toggleFilterTag(tag) {
    if (this.filterTags.includes(tag)) {
      this.filterTags = this.filterTags.filter((t) => t !== tag);
    } else {
      this.filterTags = [...this.filterTags, tag];
    }
  }

  @action
  async print() {
    this.printing = true;

    // Gather styles and HTML.
    let styles = this.printStyles(this.landscape) + getDynamicStyles();
    let printHTML = this.element.querySelector('div.report').innerHTML;

    if (this.signatureAvailable && this.includeSignature) {
      printHTML = await this.insertSignature(printHTML);
    }

    // Inject styles and HTML into the iframe.
    let printer = this.element.querySelector('iframe');
    printer.contentDocument.title = this.title;
    printer.contentDocument.body.className = 'report-print';
    printer.contentDocument.body.innerHTML = styles + printHTML;

    // Trigger the actual printing ~500ms after content insertion.
    later(printer.contentWindow, 'print', 1500);

    // Delay slightly longer before closing and resetting, to avoid a
    // race condition which sometimes closes the dialog without printing.
    later(this, 'closeDialog', 2000);
  }
}
